<template>
  <div id="app-container" class="points-page">
    <div id="contents-header">
      <Breadcrumb :current="meta" :items="PointsService.breadcrumb.value.items"></Breadcrumb>

      <h1 class="title">
        <span class="name">{{ meta.label }}</span>
      </h1>
    </div>

    <div id="main-container">
      <p class="summary" v-if="PointsService.message.value">
        {{ PointsService.message.value }}
      </p>
      <p class="summary" v-else-if="!PointsService.loading.value && PointsService.total.value">
        <span class="count" v-text="PointsService.total.value"></span>{{ messages.points_summary_found_points }}
      </p>
      <p class="summary" v-else-if="!PointsService.loading.value && !PointsService.total.value">
        {{ messages.points_summary_not_found_points }}
      </p>
      <p class="summary" v-else>
        {{ messages.common_loading }}
      </p>

      <div class="filters uk-grid-row-small uk-grid-column-medium" v-if="usePointFilter && PointsService.isInitialized.value" uk-grid>
        <div class="filter-opener filter">
          <button class="open-filter uk-button" @click="onOpenAreas" v-wave>
            <i class="material-symbols-sharp filled" data-icon="map"></i>
            <span>{{ messages.points_search_by_prefs }}</span>
          </button>
        </div>

        <div class="current-location sub filter">
          <button id="PanToCurrentLocation" class="open-filter uk-button"
            :class="{
              highlight: PointsService.locations.value.loading || PointsService.locations.value.current,
              loading: PointsService.locations.value.loading && !PointsService.locations.value.current,
              completed: PointsService.locations.value.current
            }"
            @click="onCurrentLocationClicked"
            v-wave>
            <span>{{ messages.points_current_location }}</span>
            <i class="material-symbols-sharp" data-icon="check" v-if="PointsService.locations.value.current"></i>
            <i class="material-symbols-sharp" data-icon="more_horiz" v-else-if="PointsService.locations.value.loading"></i>
            <i class="material-symbols-sharp" data-icon="my_location" v-else></i>
          </button>
        </div>

        <div class="filter-opener sub filter">
          <button class="open-filter uk-button" :class="{completed: isFiltered}" @click="onOpenPointFilter" v-wave>
            <i class="material-symbols-sharp" data-icon="check" v-if="isFiltered"></i>
            <i class="material-symbols-sharp" data-icon="filter_list" v-else></i>
            <span>{{ messages.points_open_fiters }}</span>
          </button>
        </div>
      </div>

      <div class="loading-points" v-if="PointsService.loading.value">
        <div class="uk-margin-auto" uk-spinner="ratio: 2"></div>
      </div>

      <Points
        :points="PointsService.points.value"
        :formats="PointsService.config.points?.point?.formats"
        :searchConditions="PointsService.config.points?.point?.search_conditions"
        @onPointSelected="onPointSelected"
        v-show="!PointsService.loading.value && PointsService.isInitialized.value"></Points>

      <Pagination
        @onPageChanged="onPageChanged"
        :limit="limit"
        :page="PointsService.page.value"
        :point-total="PointsService.total.value"
        v-if="PointsService.isInitialized.value">
      </Pagination>
    </div>

    <!-- 絞り込み -->
    <div id="FiltersModal" uk-modal v-if="usePointFilter && PointsService.isInitialized.value">
      <div class="uk-modal-dialog">
        <div class="uk-modal-header uk-flex uk-flex-between">
          <p class="uk-modal-title-small">{{ messages.points_open_fiters }}</p>
          <button class="uk-modal-close uk-modal-close-relative" type="button" uk-close></button>
        </div>

        <div class="uk-modal-body" uk-overflow-auto>
          <Filters :selectable="true">
            <template #filter-group-name="props">
              <slot name="filter-group-name" :filter="props.filter" :selectable="props.selectable"></slot>
            </template>
          </Filters>
        </div>

        <div class="uk-modal-footer uk-flex">
          <button class="reset uk-button" @click="onResetFilterClicked" v-wave>
            {{ messages.points_reset_fiters }}
          </button>
          <button class="close uk-button" @click="onFilterClosed" v-wave>
            {{ messages.common_close }}
          </button>
        </div>
      </div>
    </div>

    <!-- 都道府県から探す -->
    <div id="AreasModal" uk-modal v-if="usePointFilter && PointsService.areas.value.areas">
      <div class="uk-modal-dialog">
        <div class="uk-modal-header uk-flex uk-flex-between">
          <p class="uk-modal-title-small">{{ messages.points_search_by_prefs }}</p>
          <button class="uk-modal-close uk-modal-close-relative" type="button" uk-close></button>
        </div>

        <div class="uk-modal-body" uk-overflow-auto>
          <AreaSelector :areas="PointsService.areas.value.areas"></AreaSelector>
        </div>

        <div class="uk-modal-footer uk-flex">
          <button class="reset uk-button" @click="onAreasClosed" v-wave>
            {{ messages.common_close }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * 店舗を一覧・リストで検索するページ
 * 
 * 都道府県から探す時の入口に使う
 * 新店の紹介に使うことがある
 */
import uikit from "uikit"
import { computed, onMounted, ref, watch } from 'vue'

import Filters from '@/components/common/Filters.vue'

import Pagination from '@/components/points/Pagination.vue'
import Breadcrumb from '@/components/common/Breadcrumb.vue'
import Points from '@/components/common/Points.vue'
import AreaSelector from './AreaSelector.vue'

import PointsService from "@/services/PointsService.ts"
import { LocationManager } from "@/services/Location"

import Page from '@/services/Page'

import store from '@/store'


Page.storage.save('points', location.href)

const messages = computed(() => storelocator.messages)

/**
 * 絞り込みの選択状態
 */
const filtersStore = computed(() => store.getters.filters)

/**
 * 絞り込みを変更したら即時反映
 * ためて適用してもいいが、復元も含めて状態簡易が極めて複雑になる、トリガーも拾いにくい
 * UX的にも特に貯める必要はない
 */
watch(() => filtersStore, () => {
  // console.debug('filtersStore changed', filtersStore.value)
  onPageChanged(1)
  PointsService.applyFiltersToPoints()
}, { deep: true })

/**
 * ポイント絞り込みのモーダル
 */
let filterModal = null

/**
 * 都道府県を選ぶモーダル
 */
let areasModal = null

/**
 * windowサイズの監視関数
 * TODO 必要？マップが絡まないページであれば消せるように感じる？
 */
let resizeTimer = null

const interval = Math.floor(1000 / 60 * 10)

function watchWindowSize() {
  store.dispatch('onWindowResized')

  window.addEventListener("resize", () => {
    if (resizeTimer !== null) {
      clearTimeout(resizeTimer)
    }

    resizeTimer = setTimeout(() => {
      store.dispatch('onWindowResized')
    }, interval)
  })
}

/**
 * true: 絞り込み中
 */
const isFiltered = computed(() => PointsService.isFiltered())

/**
 * リセットクリック時
 */
function onResetFilterClicked() {
  try {
    PointsService.resetFilter()
  } finally {
    // resetFilter() が例外を投げても、リセットボタンの押下は計測する
    storelocator.analytics.client?.send('ResetFilter')
  }

  uikit.notification.closeAll()
  uikit.notification({
    message: messages.value.points_reset_fiters_completed,
    pos: 'top-center',
    timeout: 3000
  })
}

/**
 * Points.emits.onPointSelected
 * @param {*} point 
 */
function onPointSelected(point) {
  // console.debug('onPointSelected', point)

  storelocator.analytics.client?.send('SelectPointOnPoints', {
    PointName: point.name,
    PointKey: point.key,
    PointAddress: point.address,
  })
}

/**
 * 1ページに表示する件数
 */
const limit = computed(() => {
  return PointsService.pagination.limit ? PointsService.pagination.limit : 0
})

/**
 * true: 絞り込みを有効化
 */
const usePointFilter = ref(PointsService.config?.filters?.frontend)

/**
 * このページのメタ情報
 */
const meta = computed(() => {
  return {
    label: getTitlePrefix() + messages.value.points_title
  }
})

/**
 * ページ変更
 * @param {Number} p ページ
 */
async function onPageChanged(p) {
  PointsService.page.value = p
  PointsService.applyFiltersToPoints()

  document.body.scrollTo(0, 0)
}

/**
 * 絞り込みモーダルを開く
 */
function onOpenPointFilter() {
  filterModal.show()
}

/**
 * 都道府県を選ぶモーダルを開く
 */
function onOpenAreas() {
  areasModal.show()
}

/**
 * 絞り込みモーダルを閉じる
 */
function onFilterClosed() {
  filterModal.hide()
}

/**
 * 都道府県を選ぶモーダルを閉じる
 */
function onAreasClosed() {
  areasModal.hide()
}

/**
 * 現在地から近い順に並べるクリック時
 */
async function onCurrentLocationClicked() {
  console.debug('onCurrentLocationClicked')

  if (PointsService.locations.value.current) {
    PointsService.resetLocation()
    onPageChanged(1)
    PointsService.applyFiltersToPoints()
    return
  }

  try {
    PointsService.locations.value.current = await PointsService.panToCurrentLocation()
    onPageChanged(1)
    PointsService.applyFiltersToPoints()
  } catch (e) {
    console.warn(e)

    // 失敗・タイムアウトは共通
    let message = messages.value.common_failed_to_get_current_location

    // 拒否は明示的にわかるように、地名検索がないので誘導はなし、都道府県にしても範囲が広い
    if (e.code === LocationManager.CURRENT_LOCATION_RESULT_DENIED) {
      message = messages.value.common_denied_to_get_current_location
    } else {
      console.warn(e)
    }

    uikit.notification({
      message: message,
      pos: 'top-center',
      timeout: 3000
    })
  }
}

/**
 * @returns {String} タイトルのプレフィックスを返す
 * 
 * TODO 多言語考慮
 */
function getTitlePrefix() {
  if (PointsService.areas.value.area) {
    return PointsService.areas.value.area.area_name + 'の'
  }

  return ''
}

onMounted(async() => {
  PointsService.init()

  watchWindowSize()
  PointsService.resetFilter()
  watchWindowSize()

  PointsService.applyUrlQuery()
  await PointsService.onInitialized()

  // 絞り込みモーダル初期化とポイント絞り込み監視
  if (PointsService.config?.filters?.frontend) {
    filterModal = uikit.modal(`#FiltersModal`, {"stack": true})
    areasModal = uikit.modal(`#AreasModal`, {"stack": true})

    if (PointsService.storage.areas.load()) {
      areasModal.show()
    }
  }
})

</script>

<style lang="scss">
#app-container.points-page {
  flex-direction: column;
  background-color: #fff;
  height: auto;

  margin: 0 auto;
  max-width: 1024px;
}

.points-page {
  background-color: #eee;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .filters {
    margin: 1rem auto 0.5rem auto;
    .filter {
      display: inline-block;
      margin: .5rem 0;
      padding-left: 1rem;
      @media screen and (max-width: $breakpoint-small) {
        width: 100%;
        padding-left: 0;
      }
      button {
        border-radius: 20px;
        background-color: #fff;
        border: 1px solid #ddd;
        padding-left: 2.8rem;
        position: relative;
        transition: all 0.25s;

        @media screen and (max-width: $breakpoint-small) {
          margin: 0 0 0 1rem;
          width: calc(100% - 2rem);
        }

        @media screen and (min-width: $breakpoint-small) {
          &:hover {
            background-color: var(--theme-color);
            color: var(--text-color);
            border-color: #fff;
          }
        }

        &:active {
          background-color: var(--theme-color);
          color: var(--text-color); 
        }

        i {
          position: absolute;
          top: 50%;
          left: .75rem;
          transform: translateY(-50%);
          opacity: .7;
          transition: all 0.25s;
        }

        &.highlight {
          i {
            opacity: 1;
          }
        }

        @keyframes points-loading-loop-current-locatio {
          0% {
            clip: rect(0px, 5px, 12px, 0px);
          }
          50% {
            clip: rect(0px, 16px, 24px, 0px);
          }
          100% {
            clip: rect(0px, 24px, 24px, 0px);
          }
        }
        &.loading {
          i {
            animation: points-loading-loop-current-locatio .75s infinite;
          }
        }
      }
    }
    @media screen and (max-width: $breakpoint-small) {
      margin: 1rem auto 0.25rem auto;
      .filter {
        &.sub {
          margin: .5rem 0 1rem 0;
          width: 60%;

          &.filter-opener {
            width: 40%;
            button {
              padding: 0 1rem;
              margin: 0 0 0 0;
              width: calc(100% - 1rem);
              i {
                display: none;
              }
            }
          }
        }
      }
    }

    .area-selector {
      button {
        border: 1px solid #ddd;
      }
    }
    .open-filter {
      border-radius: 20px;
      background-color: #fff;
      border: 1px solid #ddd;
      &.completed {
        background-color: var(--theme-color);
        border-color: var(--theme-color);
        color: var(--text-color);
      }
    }
    .current-location {
      .completed {
        cursor: default; 
      }
    }
  }

  .summary {
    margin: 0 1rem;
    font-size: 0.9rem;
    height: 2rem;
    .count{
      font-size: 1.5rem;
      margin: 0 0.25rem 0 0;
    }
  }

  .loading-points {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    margin: 5rem 0;
  }

  .title {
    font-size: 1.5rem;
    margin: 2rem 1rem;
    display: flex;
    align-items: center;
    .material-symbols-sharp {
      font-size: 2.5rem;
      margin-right: .5rem;
    }
  }

  .list-result-message {
    margin: 2rem auto 3rem auto;
    font-size: .875rem;
    text-align: center;
  }

  .overlay-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    color: white;
    font-size: .875em;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.3);
  
    .points-overlay {
      box-sizing: border-box;
      max-width: calc(100% - 4rem);
      background-color: rgba(0, 0, 0, 0.6);
      padding: 1rem;
      border-radius: .25rem;
      text-align: center;
      p {
        color: #fff;
      }
    }
  }

  .points {
    .point-wrapper {
      &:first-child {
        border-top: 1px solid #eee;
      }
    }
  }
}

#FiltersModal, #AreasModal {
  .uk-modal-dialog {
    .uk-modal-header {
      padding: 1rem 1.5rem 1rem 1rem;
      p {
        font-size: 1.125rem;
      }
    }
    .uk-modal-body {
      padding: 0;
      a {
        color: #444;
      }
    }
    .uk-modal-footer {
      padding: 1rem;

      justify-content: center;
      gap: 0 1rem;

      button {
        border-radius: 20px;
      }

      .reset {
        background-color: #fff;
        transition: all 0.25s;
        &:hover {
          background-color: #eee;
        }
      }
    }
  }
}
</style>